import React from 'react';

import {
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  InputLabel,
  // Slide,
  TextField,
} from '@material-ui/core';

// import { TransitionProps } from '@material-ui/core/transitions';
import { ButtonWithLoader } from '../../../components/atoms';
import ButtonPayStripe from '../../../components/organisms/PaymentOptions/ButtonPayStripe';
import {
  PickupLocationOption,
  PreOrderTime,
  TableServiceCategory,
} from '../../../weborder/Api';
import { BLANK } from '../../../weborder/constants/Constants';
import { basketSelectors } from '../../../weborder/redux';

// const Transition = React.forwardRef<unknown, TransitionProps>(
//   function Transition(props, ref) {
//     return <Slide direction="down" ref={ref} {...props} />;
//   },
// );

type PreOrderTimeProps = {
  isDelivery: boolean;
  isTableService: boolean;
  onChange(change: string): void;
  options: PreOrderTime[];
  translate: any;
  value: string;
};

const PreOrderTimeSelect = (props: PreOrderTimeProps) => {
  const {
    isDelivery,
    isTableService,
    onChange,
    options,
    translate,
    value,
  } = props;

  return (
    <FormControl fullWidth margin="dense">
      <InputLabel
        disableAnimation
        focused
        htmlFor="When_do_you_want_to_collect"
        shrink={false}
      >
        {isDelivery
          ? translate('When_do_you_want_your_order_delivered')
          : isTableService
          ? translate('When_do_you_want_to_receive_your_order')
          : translate('When_do_you_want_to_collect')}
      </InputLabel>
      <TextField
        fullWidth
        inputProps={{
          name: 'preOrderTime_input',
          id: 'When_do_you_want_to_collect',
        }}
        margin="dense"
        onChange={(evt) => {
          onChange(evt.target.value);
        }}
        select
        SelectProps={{
          native: true,
        }}
        value={value}
        variant="outlined"
      >
        <option key={BLANK} value={BLANK} disabled>
          {props.translate('Select_a_time')}
        </option>
        {options.map((o) => (
          <option value={o.Key} key={o.Key} disabled={o.IsFull}>
            {o.IsFull ? `${o.Value} (${translate('Full')})` : o.Value}
          </option>
        ))}
      </TextField>
    </FormControl>
  );
};

type NameInputProps = {
  hasRequestFullNameFeatureFlag: boolean;
  isDelivery: boolean;
  isTableService: boolean;
  onChange(change: string): void;
  translate: any;
  value: string;
};

const NameInput = (props: NameInputProps) => {
  const {
    hasRequestFullNameFeatureFlag,
    isDelivery,
    isTableService,
    onChange,
    translate,
    value,
  } = props;

  return (
    <FormControl fullWidth margin="dense">
      <InputLabel
        shrink={false}
        disableAnimation
        htmlFor="For_when_you_collect"
        color="primary"
        focused
      >
        {hasRequestFullNameFeatureFlag
          ? `${translate('Full_name')}`
          : `${translate('Name')}`}
        {`${
          isDelivery
            ? ' (' + translate('If_the_driver_needs_to_call') + ')'
            : isTableService
            ? ''
            : ' (' + translate('For_when_you_collect') + ')'
        }`}
      </InputLabel>
      <TextField
        fullWidth
        inputProps={{
          name: 'name_input',
          id: 'For_when_you_collect',
        }}
        margin="dense"
        onChange={(evt) => {
          onChange(evt.target.value);
        }}
        value={value}
        variant="outlined"
      />
    </FormControl>
  );
};

type TableNumberInputProps = {
  category: TableServiceCategory;
  onChange(change?: number): void;
  options?: PickupLocationOption[];
  translate: any;
  value?: number;
};
const TableNumberInput = (props: TableNumberInputProps) => {
  const { category, onChange, options, translate, value } = props;

  return (
    <FormControl fullWidth>
      <InputLabel
        disableAnimation
        focused
        htmlFor="For_when_you_collect"
        shrink={false}
      >
        {`${translate(`Enter_table_number_${category}`)}`}
      </InputLabel>
      {options && options.length ? (
        <TextField
          fullWidth
          inputProps={{
            name: 'tableNumber',
            id: 'tableNumber',
            'data-fd': 'tableNumber_select',
          }}
          margin="dense"
          onChange={(evt) => {
            onChange(parseInt(evt.target.value) || undefined);
          }}
          select
          SelectProps={{
            native: true,
          }}
          value={value}
          variant="outlined"
        >
          {options
            .sort((a, b) => a.DisplayOrder - b.DisplayOrder)
            .map((o) => (
              <option
                disabled={o.Value.startsWith('---')}
                key={o.PickupLocationOptionId}
                value={o.PickupLocationOptionId}
              >
                {o.Value}
              </option>
            ))}
        </TextField>
      ) : (
        <TextField
          fullWidth
          inputProps={{
            id: 'tableNumber',
            name: 'tableNumber',
            pattern: '[0-9]*',
            'data-fd': 'tableNumber_input',
          }}
          margin="dense"
          onChange={(evt) => {
            onChange(
              parseInt(evt.target.value.replace(/\D/g, '')) || undefined,
            );
          }}
          placeholder={translate(`Table_number_${category}`) as string}
          value={typeof value === 'number' ? value : ''}
          variant="outlined"
        />
      )}
    </FormControl>
  );
};

type SelectTimeModalProps = {
  disabled?: boolean;
  loading?: boolean;
  nameInputProps?: NameInputProps;
  onCancel: () => void;
  onOk: () => void;
  preOrderTimeProps?: PreOrderTimeProps;
  selectedPayment: ReturnType<
    typeof basketSelectors.getSelectedPaymentOrDefault
  >;
  show: boolean;
  tableNumberInputProps?: TableNumberInputProps;
  translate: any;
};

export const SelectTimeModal = (props: SelectTimeModalProps): JSX.Element => {
  const {
    disabled,
    loading,
    nameInputProps,
    preOrderTimeProps,
    selectedPayment,
    tableNumberInputProps,
    translate,
  } = props;

  const onOk = () => {
    props.onOk();
  };
  const onCancel = () => {
    props.onCancel();
  };

  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      onClose={onCancel}
      open={props.show}
      // TransitionComponent={Transition}
    >
      <DialogContent>
        {preOrderTimeProps && <PreOrderTimeSelect {...preOrderTimeProps} />}

        {nameInputProps && <NameInput {...nameInputProps} />}

        {tableNumberInputProps && (
          <TableNumberInput {...tableNumberInputProps} />
        )}
      </DialogContent>

      <DialogActions>
        {selectedPayment && selectedPayment.paymentId === 'stripe' ? (
          <ButtonPayStripe
            data-fd="stripe_pay_confirm_button"
            disabled={disabled}
            fullWidth
            // loading={loading}
            onClick={onOk}
            size="large"
            variant="contained"
          >
            {translate('Confirm')}
          </ButtonPayStripe>
        ) : (
          <ButtonWithLoader
            data-fd="Confirm"
            disabled={disabled}
            fullWidth
            label={translate('Confirm')}
            loading={loading}
            onClick={onOk}
            size="large"
            variant="contained"
          />
        )}
      </DialogActions>
    </Dialog>
  );
};
